import { cva, type VariantProps } from 'class-variance-authority';
import { FileText, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export type ChatAttachmentKind = 'image' | 'file';

/**
 * Where a chip is standing, which is what decides how it is drawn: the user's
 * own bubble is the brand colour, everything else is a plain surface. Two sets
 * of tokens rather than transparency, so a chip is legible on both.
 */
const chipVariants = cva(
  'flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-sm',
  {
    variants: {
      tone: {
        surface: 'border-line bg-fill-faint text-ink',
        onPrimary: 'border-on-primary-line bg-on-primary-fill text-white',
      },
      interactive: {
        true: 'cursor-pointer transition',
        false: '',
      },
    },
    compoundVariants: [
      { tone: 'surface', interactive: true, className: 'hover:bg-fill' },
      {
        tone: 'onPrimary',
        interactive: true,
        className: 'hover:bg-on-primary-fill-hover',
      },
    ],
    defaultVariants: { tone: 'surface', interactive: false },
  },
);

/*
 * On the brand colour the size stays plain white: the muted-on-primary token
 * over a chip's own fill lands at 3.3:1, which the accessibility gate refuses
 * and a reader would struggle with. Being smaller is what makes it secondary.
 */
const detailVariants = cva('text-xs', {
  variants: {
    tone: { surface: 'text-ink-muted', onPrimary: 'text-white' },
  },
  defaultVariants: { tone: 'surface' },
});

export interface ChatAttachmentChipProps extends VariantProps<typeof chipVariants> {
  kind: ChatAttachmentKind;
  name: string;
  /** The size, already in words — the design system does no formatting. */
  detail?: string;
  /** Where the file can be fetched. Absent while it is still on its way up. */
  href?: string;
  /** Shown instead of the icon while the file is uploading. */
  busy?: boolean;
  /** Offered only where a file can still be taken back: the composer. */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}

/**
 * A file, named and sized.
 *
 * The same chip in both places a file appears — waiting above the composer and
 * sitting in the message it was sent with — so a file looks like itself from
 * the moment it is picked. A chip with somewhere to go is a link; one that is
 * still uploading is not, because there is nothing to open yet.
 */
export function ChatAttachmentChip({
  kind,
  name,
  detail,
  href,
  busy = false,
  onRemove,
  removeLabel,
  tone,
  className,
}: ChatAttachmentChipProps) {
  const Icon = busy ? Loader2 : kind === 'image' ? ImageIcon : FileText;

  const inside = (
    <>
      <Icon
        size={16}
        aria-hidden='true'
        className={cn('shrink-0', busy && 'animate-spin')}
      />
      <span className='min-w-0 flex-1 truncate'>{name}</span>
      {detail ? (
        <span className={cn('shrink-0', detailVariants({ tone }))}>{detail}</span>
      ) : null}
    </>
  );

  const body = href ? (
    <a
      href={href}
      target='_blank'
      rel='noreferrer'
      className={cn(chipVariants({ tone, interactive: true }), className)}
    >
      {inside}
    </a>
  ) : (
    <span className={cn(chipVariants({ tone }), className)}>{inside}</span>
  );

  if (!onRemove) {
    return body;
  }

  return (
    <span className='flex items-center gap-1'>
      {body}
      <button
        type='button'
        aria-label={removeLabel}
        onClick={onRemove}
        className='shrink-0 cursor-pointer rounded-full p-1 text-ink-muted transition hover:bg-danger-fill hover:text-danger'
      >
        <X size={14} aria-hidden='true' />
      </button>
    </span>
  );
}

export interface ChatAttachmentItem {
  kind: ChatAttachmentKind;
  name: string;
  detail?: string;
  href: string;
}

export interface ChatAttachmentsProps extends VariantProps<typeof chipVariants> {
  files: ChatAttachmentItem[];
  className?: string;
}

/**
 * The files a message carries.
 *
 * An image is shown as the picture it is — a screenshot someone shared should
 * still be a screenshot when they scroll back to it — and everything else as a
 * chip that opens the file. Either way it is one click to the file itself.
 */
export function ChatAttachments({ files, tone, className }: ChatAttachmentsProps) {
  if (files.length === 0) {
    return null;
  }

  return (
    <div data-slot='chat-attachments' className={cn('flex flex-col gap-1.5', className)}>
      {files.map((file) =>
        file.kind === 'image' ? (
          <a
            key={file.href}
            href={file.href}
            target='_blank'
            rel='noreferrer'
            className='block overflow-hidden rounded-md'
          >
            <img
              src={file.href}
              alt={file.name}
              /* Sized to sit in a bubble without pushing the reply off screen. */
              className='max-h-64 w-auto max-w-full rounded-md object-contain'
            />
          </a>
        ) : (
          <ChatAttachmentChip
            key={file.href}
            kind={file.kind}
            name={file.name}
            detail={file.detail}
            href={file.href}
            tone={tone}
          />
        ),
      )}
    </div>
  );
}
